import {
    saveEncodedValueToLocalStorage,
    removeKeyFromLocalStorage,
    getDecodedValueFromLocalStorage,
} from 'root/utils/dom/localStorage';

import { sendWizardFormFieldEventToGTM } from 'root/main/components/wizardForm/wizardFormGTM';
import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import businessTypeMap from 'root/main/components/wizardForm/businessTypeMap.json';
import { handleDomainPrefixField } from 'root/helpers/formHelpers';
import { FormGTMObjectConstructor } from 'root/utils/tracking/GA4/formTracking';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';
import { toggleSMSOptinAndFootnoteDisplayOnProductSelection } from 'root/helpers/formHelpers';
import { handlePostalCodeField } from 'root/helpers/formHelpers';

class WizardForm {
    #wizardFormContainer;
    #wizardFormEl;
    #fieldButtonsToStore;
    #form;
    #product;
    #fieldsObject;
    constructor(el) {
        this.#wizardFormContainer = el;
        this.#wizardFormEl = this.#wizardFormContainer
            ? this.#wizardFormContainer.closest('[data-form=ls-form]')
            : null;
        this.#fieldButtonsToStore = Array.from(
            this.#wizardFormContainer.querySelectorAll('[data-wizard-form-store-field]')
        );
        const formSelector = '[data-form="ls-form"]';
        this.#form =
            this.#wizardFormContainer.querySelector(formSelector) ??
            this.#wizardFormContainer.closest(formSelector);
        this.#product = document.body.dataset.product;
        this.#fieldsObject = {};
    }

    #getButtonFormStoreFieldValue(el) {
        const fieldNameAndValue = el.dataset.wizardFormStoreField;
        return {
            fieldNameAndValue,
            fieldValueArr: fieldNameAndValue?.split('|'),
        };
    }

    #isADPField(field) {
        return 'wizardFormAdpField' in field.dataset;
    }
    #showADPErrorMessage(showMessage) {
        const ADPErrorMessageEl = this.#wizardFormContainer.querySelector(
            '[data-wizard-form-adp-message]'
        );
        if (ADPErrorMessageEl) {
            ADPErrorMessageEl.classList.toggle('gb-hide', !showMessage);
            if (showMessage) {
                ADPErrorMessageEl.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }
    #setButtonSelectedState(button, businessTypeValue) {
        const [key, value] = this.#getButtonFormStoreFieldValue(button).fieldValueArr;
        if (key === 'business_type') {
            if (businessTypeValue === value) {
                button.dataset.wizardButtonSelected = 'true';
            }
        } else if (getDecodedValueFromLocalStorage(key) === value) {
            button.dataset.wizardButtonSelected = 'true';
        } else {
            button.dataset.wizardButtonSelected = 'false';
        }
    }
    #getBusinessTypeValue() {
        let businessTypeValue = getDecodedValueFromLocalStorage('business_type');
        if (!businessTypeValue)
            businessTypeValue = this.#getBusinessTypeValueFromCookie();
        return businessTypeValue;
    }
    #getBusinessTypeValueFromCookie() {
        const localStorageBTByCookieBT = new Map(Object.entries(businessTypeMap));
        const cm = new CookieMonster();
        return localStorageBTByCookieBT.get(cm.getCookie('ls_vertical_bt'));
    }

    /* The field that gets set here is referred to in this file html/src/php/Pages/ThankYou/Request.php
    this is part of the X & K series launch, where we show different watch a demo video depending on the chosen business type
    https://jira.atlightspeed.net/browse/MW-4673 */
    #setBusinessTypeSlugCookie(button) {
        if (button.dataset.wizardBusinessTypeSlug) {
            const businessTypeSlugField = this.#wizardFormEl?.querySelector(
                'input[name=business_type_slug]'
            );
            if (businessTypeSlugField && businessTypeSlugField.type === 'hidden') {
                businessTypeSlugField.value = button.dataset.wizardBusinessTypeSlug;
            }
        }
    }

    #isHardwareOptions(key) {
        return getDecodedValueFromLocalStorage(key) && key?.includes('hardware');
    }
    #setHiddenFieldForFreemiumFormHardware(form) {
        const hardwareOptions = [];
        this.#fieldButtonsToStore.forEach(button => {
            const [key] = this.#getButtonFormStoreFieldValue(button).fieldValueArr;
            if (this.#isHardwareOptions(key)) {
                hardwareOptions.push(button.dataset.wizardFormHardwareOption.trim());
            }
        });
        if (
            form.description?.type === 'hidden' &&
            form.description?.value === '' &&
            form.dataset.formName.includes('freemium')
        ) {
            form.description.value = hardwareOptions.join(' - ');
        }
    }
    #removeUnwantedContentPostIDField(el) {
        const contentPostIdFields = el.querySelectorAll('input[name="content_post_id"]');
        /* we're using include here because the agnostic form could include different suffixes */
        const isAgnosticWizardForm = this.#wizardFormEl.dataset.formName.includes(
            'other_watch_demo_wizard_lightbox'
        );

        const product = this.#fieldsObject['product'];
        const productMap = {
            retail: 'Cloud',
            restaurant: 'POSIOS',
            golf: 'Golf',
        };
        if (contentPostIdFields && isAgnosticWizardForm) {
            contentPostIdFields.forEach(field => {
                if (product && product !== productMap[field.dataset.productName]) {
                    field.remove();
                }
            });
        }
    }
    #populateHiddenFieldsAndRemoveEmptyOnes(el) {
        const hiddenFieldsToFill = Array.from(el.querySelectorAll('input[type="hidden"]'))
            .filter(field => {
                return field.value === '' || field.value === 'product-dynamic';
            })
            .filter(field => field.name !== 'series');
        if (hiddenFieldsToFill) {
            hiddenFieldsToFill.forEach(field => {
                const newFieldValue = this.#fieldsObject[field.name];
                if (newFieldValue) {
                    field.value = newFieldValue;
                } else {
                    field.remove();
                }
            });
        }
    }

    #setSelectedFieldsObject(key, value) {
        this.#fieldsObject[key] = value;
    }

    setButtonSelectedStateOnLoad() {
        return this.#fieldButtonsToStore.forEach(button =>
            this.#setButtonSelectedState(button, this.#getBusinessTypeValue())
        );
    }

    handleSecondaryBusinessTypeSelection = () => {
        const hiddenFieldContainer = this.#form.querySelector(
            '[data-form=hidden-field-container]'
        );
        const visibleSubPanelEl = this.#form.querySelector(
            '[data-wizard-sub-panel="visible"]'
        );
        let secondaryBusinessTypeHiddenField = hiddenFieldContainer?.querySelector(
            'input[name=secondary_business_type]'
        );
        if (visibleSubPanelEl) {
            const selectedSecondaryBusinessType = visibleSubPanelEl.querySelector(
                '.wizard__sub-panel-grid [data-wizard-button-selected="true"]'
            );
            const selectedValue =
                selectedSecondaryBusinessType?.dataset.wizardFormStoreField?.split(
                    '|'
                )[1];
            if (selectedSecondaryBusinessType && selectedValue) {
                if (!secondaryBusinessTypeHiddenField) {
                    secondaryBusinessTypeHiddenField = document.createElement('input');
                    secondaryBusinessTypeHiddenField.type = 'hidden';
                    secondaryBusinessTypeHiddenField.name = 'secondary_business_type';
                    hiddenFieldContainer?.appendChild(secondaryBusinessTypeHiddenField);
                }
                secondaryBusinessTypeHiddenField.value = selectedValue;
            }
        } else if (!visibleSubPanelEl && secondaryBusinessTypeHiddenField) {
            secondaryBusinessTypeHiddenField.remove();
        }
    };

    addButtonListeners() {
        const leadformSubmitButton = this.#form.querySelector(
            '[data-wizard-leadform-container] [data-select=form-submit]'
        );
        if (leadformSubmitButton) {
            leadformSubmitButton.addEventListener('click', () => {
                if (this.#form.checkValidity()) {
                    this.handleSecondaryBusinessTypeSelection();
                }
            });
        }

        if (this.#fieldButtonsToStore) {
            this.#fieldButtonsToStore.forEach(button => {
                button.addEventListener('click', e => {
                    const el = e.currentTarget;
                    const buttonFormStoreField =
                        this.#getButtonFormStoreFieldValue(button);
                    const { fieldNameAndValue } = buttonFormStoreField;

                    /* @todo this block of nested if/else statements needs refactoring */
                    if (this.#isADPField(button)) {
                        this.#showADPErrorMessage(true);
                        // Hide "next" button
                        button
                            .closest('[data-wizard-panel]')
                            .querySelector('[data-wizard-go-next]')
                            ?.classList.add('gb-hide');

                        sendWizardFormFieldEventToGTM(
                            this.#wizardFormEl,
                            e.currentTarget
                        );
                    } else if (fieldNameAndValue) {
                        this.#showADPErrorMessage(false);
                        const [key, value] = buttonFormStoreField.fieldValueArr;
                        if (
                            this.#isHardwareOptions(key) &&
                            el.dataset.wizardButtonSelected === 'true'
                        ) {
                            removeKeyFromLocalStorage(key);
                        } else {
                            this.#setSelectedFieldsObject(key, value); // used to send form data on submit
                            saveEncodedValueToLocalStorage(key, value);
                            toggleSMSOptinAndFootnoteDisplayOnProductSelection(
                                this.#form,
                                key,
                                value
                            );
                        }

                        if (button instanceof HTMLButtonElement) {
                            this.#setButtonSelectedState(
                                button,
                                this.#getBusinessTypeValue()
                            );
                        }

                        if (
                            !button.dataset.wizardFormStoreField.includes(
                                'secondary_business_type'
                            )
                        )
                            this.#setBusinessTypeSlugCookie(button);

                        handleDomainPrefixField({
                            form: this.#form,
                        }).toggleDisplay();

                        if (key === 'product') {
                            handlePostalCodeField(this.#form, value);
                        }

                        sendWizardFormFieldEventToGTM(
                            this.#wizardFormEl,
                            e.currentTarget
                        );
                    }
                });
            });
        }
    }
    addFormListener() {
        if (this.#form) {
            this.#form.addEventListener('submit', e => {
                const el = e.currentTarget;
                this.#removeUnwantedContentPostIDField(el);
                this.#setHiddenFieldForFreemiumFormHardware(this.#form);
                this.#populateHiddenFieldsAndRemoveEmptyOnes(el);
                const formGTMObjectConstructor = new FormGTMObjectConstructor(this.#form);
                GoogleTagManager.sendEvent(
                    formGTMObjectConstructor.getFormGTMObject('form_submit')
                );
            });
        }
    }
}
export default function wizardForm(wizardForms) {
    wizardForms.forEach(wizardForm => {
        const wf = new WizardForm(wizardForm);
        wf.addButtonListeners();
        wf.setButtonSelectedStateOnLoad();
        wf.addFormListener();
    });
}
